import { useEffect, useRef, useState, useId } from 'react'
import mermaid from 'mermaid'
import { Maximize2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MermaidDiagramDialog } from '@/components/MermaidDiagramDialog'

interface MermaidDiagramProps {
  chart: string
  className?: string
}

let mermaidInitialized = false

function ensureMermaidInitialized() {
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: document.documentElement.classList.contains('dark')
        ? 'dark'
        : 'default',
      securityLevel: 'strict',
    })
    mermaidInitialized = true
  }
}

export function MermaidDiagram({ chart, className }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [svg, setSvg] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const id = useId().replace(/:/g, '-')

  useEffect(() => {
    let cancelled = false

    async function renderDiagram() {
      setLoading(true)
      try {
        ensureMermaidInitialized()
        const { svg: renderedSvg } = await mermaid.render(
          `mermaid-${id}`,
          chart.trim()
        )
        if (!cancelled) {
          setSvg(renderedSvg)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Invalid mermaid syntax'
          )
          setSvg('')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    if (chart.trim()) {
      renderDiagram()
    } else {
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [chart, id])

  if (loading) {
    return (
      <div
        data-testid="mermaid-loading"
        className={cn(
          'my-4 flex items-center justify-center rounded-lg border border-border bg-muted/30 p-8',
          className
        )}
      >
        <span className="text-sm text-muted-foreground">
          Loading diagram...
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div
        data-testid="mermaid-error"
        className={cn(
          'my-4 rounded-lg border border-destructive/50 bg-destructive/10 p-4',
          className
        )}
      >
        <p className="text-sm font-medium text-destructive">
          Mermaid diagram error
        </p>
        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">
          {error}
        </pre>
        <details className="mt-2">
          <summary className="text-xs text-muted-foreground cursor-pointer">
            Show source
          </summary>
          <pre className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
            {chart}
          </pre>
        </details>
      </div>
    )
  }

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          'group relative my-4 flex justify-center [&_svg]:max-w-full cursor-pointer',
          className
        )}
        role="button"
        aria-label="Expand diagram"
        onClick={() => setDialogOpen(true)}
        title="Click to expand"
      >
        <div dangerouslySetInnerHTML={{ __html: svg }} />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/5 transition-colors rounded-lg">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 border border-border rounded-lg p-2 shadow-sm">
            <Maximize2 className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      </div>
      <MermaidDiagramDialog
        chart={chart}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  )
}
